import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui/buttons/Button';

type ProductCardProps = {
  productName: string;
  sellerName: string;
  price: number;
  unit: string;
  quantityAvailable: number;
  speciesCode: string;
  gradeCode: string;
  lengthDisplayName: string;
  onClick?: () => void;
};

export default function ProductCard({
  productName,
  sellerName,
  onClick,
  price,
  unit,
  quantityAvailable,
  speciesCode,
  gradeCode,
  lengthDisplayName,
}: ProductCardProps) {
  const { t } = useTranslation('product');
  return (
    <article
      data-testid="product-card"
      className="bg-neut-bg-alt rounded-lg border border-neut-border hover:border-[#10B981] hover:shadow-lg transition-all shadow-sm cursor-pointer"
      onClick={onClick}
    >
      <div className="h-48 bg-gradient-to-br from-amber-50 to-amber-100 rounded-t-lg flex items-center justify-center">
        <div className="text-4xl">🌲</div>
      </div>
      <div className="p-4 sm:p-6">
        <div className="mb-2">
          <h3
            data-testid="product-card-name"
            className="text-neut-prim mb-1 font-semibold truncate"
          >
            {productName}
          </h3>
          <p className="text-sm text-gray-600 hover:text-[#10B981] font-normal truncate">
            {sellerName}
          </p>
        </div>

        <div className="space-y-2 mb-4 pb-4 border-b border-gray-100">
          <p className="text-xs text-gray-600 font-normal">
            {t('product.species')}:{' '}
            <span className="text-gray-900 font-medium">{speciesCode}</span>
          </p>
          <p className="text-xs text-gray-600 font-normal">
            {t('product.grade')}:{' '}
            <span className="text-gray-900 font-medium">{gradeCode}</span>
          </p>
          <div className="flex items-start gap-1.5">
            <span className="text-xs text-gray-600 font-normal flex-shrink-0">
              {t('product.lengths')}:
            </span>
            <div className="flex flex-wrap gap-1">
              <span className="text-xs px-1.5 py-0.5 bg-prim-50 text-prim-700 rounded font-medium">
                {lengthDisplayName}
              </span>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <h4 className="mb-4">
            <span className="text-xl text-neut-prim font-semibold">
              ${price}
            </span>
            <span className="text-sm text-neut-600 font-normal"> / {unit}</span>
          </h4>

          {(quantityAvailable || 0) > 0 ? (
            <span className="text-xs px-2 py-1 rounded-full font-medium bg-prim-100 text-prim-600">
              {t('product.in_stock')}
            </span>
          ) : (
            <span className="text-xs px-2 py-1 rounded-full font-medium bg-error-100 text-error-600">
              {t('product.out_of_stock')}
            </span>
          )}
        </div>
      </div>

      <footer className="px-4 sm:px-6 pb-4 sm:pb-6">
        <Button variant="primary" className="gap-2" onClick={onClick}>
          <Eye size={18} aria-hidden="true" />
          {t('product.view_details')}
        </Button>
      </footer>
    </article>
  );
}
